import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { StatCard } from "@/components/common/Common";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, Legend,
} from "recharts";
import { Building2, Package, AlertTriangle, ShoppingCart, DollarSign, Users, TrendingUp, Store } from "lucide-react";

const fmt = (n) => `TZS ${Number(n || 0).toLocaleString()}`;

function ChartCard({ title, children }) {
  return (
    <div className="bg-card border border-border rounded-md p-5">
      <h3 className="font-heading font-semibold text-sm mb-4">{title}</h3>
      {children}
    </div>
  );
}

function RecentSales({ sales, t, showBranch }) {
  const navigate = useNavigate();
  return (
    <div className="bg-card border border-border rounded-md p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold text-sm">{t("recentSales")}</h3>
        <button className="text-xs text-primary hover:underline" onClick={() => navigate("/sales")} data-testid="dash-view-sales">View all</button>
      </div>
      <div className="space-y-3">
        {sales.length === 0 && <p className="text-xs text-muted-foreground">{t("noSales")}</p>}
        {sales.map((s) => (
          <div key={s.id} className="flex items-center justify-between text-sm border-b border-border last:border-0 pb-2 last:pb-0">
            <div className="min-w-0">
              <p className="font-medium truncate">{s.invoice_no} · {s.customer_name}</p>
              <p className="text-xs text-muted-foreground">{showBranch ? `${s.branch_name} · ` : ""}{new Date(s.created_at).toLocaleString()}</p>
            </div>
            <span className="font-semibold text-primary whitespace-nowrap ml-3">{fmt(s.total)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TopProducts({ data, t }) {
  return (
    <ChartCard title={t("topProducts")}>
      {data.length === 0 ? <p className="text-xs text-muted-foreground">{t("noSales")}</p> : (
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={data} layout="vertical" margin={{ left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
            <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
            <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
            <Bar dataKey="qty" fill="hsl(var(--chart-2))" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
}

function SalesTrend({ data, t }) {
  return (
    <ChartCard title={t("salesTrend")}>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
          <XAxis dataKey="day" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
          <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
          <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} formatter={(v, n) => n === "revenue" ? fmt(v) : v} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Line type="monotone" dataKey="revenue" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="orders" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function SuperAdminDashboard({ t }) {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/dashboard/super-admin").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-28 bg-muted rounded-md animate-pulse" />)}</div>;
  const c = data.cards;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard label={t("revenue")} value={fmt(c.revenue)} hint={`${t("todayRevenue")}: ${fmt(c.today_revenue)}`} icon={DollarSign} accent="success" testId="stat-revenue" onClick={() => navigate("/sales")} />
        <StatCard label={t("totalSales")} value={c.total_sales} hint={`${t("todaySales")}: ${c.today_sales}`} icon={ShoppingCart} accent="primary" testId="stat-total-sales" onClick={() => navigate("/sales")} />
        <StatCard label={t("totalProducts")} value={c.total_products} icon={Package} accent="primary" testId="stat-total-products" onClick={() => navigate("/products")} />
        <StatCard label={t("lowStock")} value={c.low_stock} icon={AlertTriangle} accent="warning" testId="stat-low-stock" onClick={() => navigate("/products")} />
        <StatCard label={t("totalBranches")} value={c.total_branches} icon={Store} accent="primary" testId="stat-total-branches" onClick={() => navigate("/branches")} />
        <StatCard label={t("totalUsers")} value={c.total_users} icon={Users} accent="primary" testId="stat-total-users" onClick={() => navigate("/users")} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2"><SalesTrend data={data.sales_trend} t={t} /></div>
        <TopProducts data={data.top_products} t={t} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <ChartCard title={t("revenueByBranch")}>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.revenue_by_branch}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="branch" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} formatter={(v) => fmt(v)} />
                <Bar dataKey="revenue" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
        <RecentSales sales={data.recent_sales} t={t} showBranch />
      </div>
    </div>
  );
}

function BranchDashboard({ t, branchId }) {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { setData(null); api.get(`/dashboard/branch?branch_id=${branchId}`).then((r) => setData(r.data)).catch(() => {}); }, [branchId]);
  if (!data) return <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-28 bg-muted rounded-md animate-pulse" />)}</div>;
  const c = data.cards;
  return (
    <div className="space-y-6">
      <div className="bg-primary/5 border border-primary/20 rounded-md p-4">
        <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("currentBranch")}</p>
        <h2 className="font-heading font-bold text-xl">{data.branch.name}</h2>
        <p className="text-sm text-muted-foreground">{data.branch.location} · {data.branch.code}</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard label={t("todayRevenue")} value={fmt(c.today_revenue)} icon={TrendingUp} accent="success" testId="stat-branch-today-rev" onClick={() => navigate("/sales")} />
        <StatCard label={t("revenue")} value={fmt(c.revenue)} icon={DollarSign} accent="success" testId="stat-branch-revenue" onClick={() => navigate("/sales")} />
        <StatCard label={t("todaySales")} value={c.today_sales} icon={ShoppingCart} accent="primary" testId="stat-branch-today-sales" onClick={() => navigate("/sales")} />
        <StatCard label={t("totalSales")} value={c.total_sales} icon={ShoppingCart} accent="primary" testId="stat-branch-sales" onClick={() => navigate("/sales")} />
        <StatCard label={t("totalProducts")} value={c.total_products} icon={Package} accent="primary" testId="stat-branch-products" onClick={() => navigate("/products")} />
        <StatCard label={t("lowStock")} value={c.low_stock} icon={AlertTriangle} accent="warning" testId="stat-branch-lowstock" onClick={() => navigate("/products")} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2"><SalesTrend data={data.sales_trend} t={t} /></div>
        <TopProducts data={data.top_products} t={t} />
      </div>
      <RecentSales sales={data.recent_sales} t={t} />
    </div>
  );
}

export default function DashboardPage() {
  const { t, activeBranchId } = useApp();
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">{t("dashboard")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("salesOverview")}</p>
      </div>
      {activeBranchId ? <BranchDashboard t={t} branchId={activeBranchId} /> : <SuperAdminDashboard t={t} />}
    </div>
  );
}
